import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import {
  getAppUpdates,
  publishAppUpdate,
  deleteAppUpdate,
  updateAppUpdateReleaseNotes,
  pushUpdateToModel,
  downloadAppUpdateUrl,
  rollbackAppUpdate,
  getOtaSelfCheckEnabled,
  setOtaSelfCheckEnabled,
  ApiResponseError,
  AppUpdate,
  DeviceModel,
} from '../../services/admin';
import { createExclusiveRunner, describeUploadError, formatBytes, formatTimestamp } from './apkArtifact';
import {
  ArtifactDetails,
  ArtifactStatusBadge,
  PublishResult,
  ReleaseActions,
  UploadForm,
  UploadRequest,
} from './ApkArtifactViews';

interface Props {
  models: DeviceModel[];
}

/** APK Repository (Settings > Model Catalog > "APK Repository") - published Android builds, one
 * per hardware model, that radios of that model check for and silently self-install OTA (see
 * util/AppUpdateManager.kt, documentation/protocol.md "OTA app updates"). Each entry can also be pushed
 * immediately to every currently-connected device of its model, instead of waiting for their own
 * periodic check. */
export default function ApkRepositoryManager({ models }: Props) {
  const [updates, setUpdates] = useState<AppUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  // Remounts the upload form (fresh inputs) every time it is opened.
  const [formKey, setFormKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // The release the server created, shown with its APK-derived identity until the admin closes it.
  const [published, setPublished] = useState<AppUpdate | null>(null);
  const uploadOnce = useRef(createExclusiveRunner()).current;
  const [pushingId, setPushingId] = useState<string | null>(null);
  const [rollingBackId, setRollingBackId] = useState<string | null>(null);
  const [editingNotesId, setEditingNotesId] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState('');
  const [savingNotesId, setSavingNotesId] = useState<string | null>(null);
  const [otaSelfCheckEnabled, setOtaSelfCheckEnabledState] = useState(true);
  const [otaTogglePending, setOtaTogglePending] = useState(false);

  // Which models' version history is currently expanded, keyed by device_model_id. Never reset by
  // load()/polling - only user clicks change it, so an open history stays open across background
  // refreshes (see documentation/decisions.md "APK Repository version history & downgrade support").
  const [expandedModels, setExpandedModels] = useState<Set<string>>(new Set());
  // Releases whose artifact details are open, keyed by release id - also kept across polling.
  const [expandedReleases, setExpandedReleases] = useState<Set<string>>(new Set());

  const toggleRelease = (id: string) => {
    setExpandedReleases((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleModel = (deviceModelId: string) => {
    setExpandedModels((prev) => {
      const next = new Set(prev);
      if (next.has(deviceModelId)) next.delete(deviceModelId);
      else next.add(deviceModelId);
      return next;
    });
  };

  // One group per model: the current/recommended release (is_active, falling back to the newest
  // by version_code if none is active - never assumed to be "Current" in that fallback case) shown
  // as the collapsed row, every other release (every retained release is still kept - see
  // documentation/decisions.md) as history, newest first. Sorts copies, never the source arrays.
  const groupedUpdates = useMemo(() => {
    const byModel = new Map<string, AppUpdate[]>();
    updates.forEach((u) => {
      const list = byModel.get(u.device_model_id) ?? [];
      list.push(u);
      byModel.set(u.device_model_id, list);
    });
    return Array.from(byModel.values()).map((releases) => {
      const sorted = [...releases].sort((a, b) => b.version_code - a.version_code);
      const primary = releases.find((r) => r.is_active) ?? sorted[0];
      return {
        deviceModelId: primary.device_model_id,
        modelName: primary.model_name,
        primary,
        history: sorted.filter((r) => r.id !== primary.id),
      };
    });
  }, [updates]);

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      setUpdates(await getAppUpdates());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load APK repository');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    getOtaSelfCheckEnabled().then(setOtaSelfCheckEnabledState).catch(() => {});
    const interval = setInterval(() => {
      if (!showForm) load(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [load, showForm]);

  const handleToggleOtaSelfCheck = async () => {
    setOtaTogglePending(true);
    setError(null);
    try {
      setOtaSelfCheckEnabledState(await setOtaSelfCheckEnabled(!otaSelfCheckEnabled));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update OTA self-check setting');
    } finally {
      setOtaTogglePending(false);
    }
  };

  const openAdd = () => {
    setUploadError(null);
    setPublished(null);
    setFormKey((k) => k + 1);
    setShowForm(true);
  };

  const closeForm = () => {
    if (uploading) return;
    setShowForm(false);
    setUploadError(null);
    setPublished(null);
  };

  // One request at a time (uploadOnce), no client timeout: the server answers only after the
  // upload has been validated and published - or refused, in which case nothing was published.
  const handleUpload = (req: UploadRequest) =>
    uploadOnce(async () => {
      setUploading(true);
      setUploadError(null);
      try {
        setPublished(await publishAppUpdate(req));
        load(true);
      } catch (err) {
        setUploadError(
          err instanceof ApiResponseError ? describeUploadError(err.status, err.body) : describeUploadError(null, err instanceof Error ? err.message : '')
        );
      } finally {
        setUploading(false);
      }
    });

  const handleDelete = async (u: AppUpdate) => {
    if (!confirm(`Delete "${u.version_name}" (${u.model_name ?? 'unknown model'}) from the repository?`)) return;
    setError(null);
    try {
      await deleteAppUpdate(u.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete APK');
    }
  };

  const startEditingNotes = (u: AppUpdate) => {
    setEditingNotesId(u.id);
    setEditingNotes(u.release_notes ?? '');
    setError(null);
  };

  const cancelEditingNotes = () => {
    setEditingNotesId(null);
    setEditingNotes('');
  };

  const saveEditingNotes = async (u: AppUpdate) => {
    setSavingNotesId(u.id);
    setError(null);
    try {
      await updateAppUpdateReleaseNotes(u.id, editingNotes.trim());
      cancelEditingNotes();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update release notes');
    } finally {
      setSavingNotesId(null);
    }
  };

  const handlePushToModel = async (u: AppUpdate) => {
    if (!u.model_name) return;
    if (
      !confirm(
        `Update ALL ${u.model_name} devices to "${u.version_name}" now? Connected devices update immediately; offline devices update the moment they come back online.`
      )
    )
      return;
    setPushingId(u.id);
    setError(null);
    setInfo(null);
    try {
      const result = await pushUpdateToModel(u.model_name);
      setInfo(
        `Pushed to ${result.pushed} of ${result.total} ${u.model_name} device(s) now; the remaining ${result.offline} will update as soon as they reconnect.`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to push update');
    } finally {
      setPushingId(null);
    }
  };

  // Marks a previously-published release as the current/recommended one for its model - either
  // direction (an older release becomes a deliberate downgrade target, a newer one is a normal
  // promotion). Never deletes any release - see documentation/decisions.md.
  const handleSetActive = async (u: AppUpdate) => {
    const isDowngrade = updates.some((o) => o.device_model_id === u.device_model_id && o.is_active && o.version_code > u.version_code);
    const verb = isDowngrade ? 'Roll back' : 'Set';
    if (
      !confirm(
        `${verb} ${u.model_name ?? 'this model'} to "${u.version_name}" (${u.version_code}) as the current/recommended release? Every device of this model will self-install it on its next update check.`
      )
    )
      return;
    setRollingBackId(u.id);
    setError(null);
    setInfo(null);
    try {
      await rollbackAppUpdate(u.id);
      setInfo(`"${u.version_name}" is now the current/recommended release for ${u.model_name ?? 'this model'}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update the current release');
    } finally {
      setRollingBackId(null);
    }
  };

  // One row per release. `opts` distinguishes the collapsed/primary row (model name + optional
  // expand chevron) from an indented history row (blank model cell) - action buttons only depend
  // on the release's own is_active flag, not on whether it happens to be the primary row, so a
  // primary release that is genuinely not active (no release marked active for its model) still
  // correctly gets "Set as Current" instead of "Update ALL".
  const renderReleaseRow = (
    u: AppUpdate,
    opts: { modelName?: string | null; deviceModelId?: string; hasHistory?: boolean; expanded?: boolean; indent?: boolean } = {}
  ) => (
    <Fragment key={u.id}>
      <tr className="hover:bg-gray-700/30">
        <td className="px-4 py-3 font-mono font-semibold">
          {opts.indent ? (
            <span className="pl-5 text-gray-600">↳</span>
          ) : opts.hasHistory ? (
            <button
              type="button"
              onClick={() => toggleModel(opts.deviceModelId!)}
              className="flex items-center gap-2 hover:text-blue-300"
              aria-label={opts.expanded ? `Collapse ${opts.modelName ?? 'model'} version history` : `Expand ${opts.modelName ?? 'model'} version history`}
              title={opts.expanded ? 'Collapse version history' : 'Expand version history'}
            >
              <span className={`inline-block transition-transform ${opts.expanded ? 'rotate-90' : ''}`}>▶</span>
              {opts.modelName ?? '—'}
            </button>
          ) : (
            opts.modelName ?? '—'
          )}
        </td>
        <td className="px-4 py-3">
          {u.version_name} <span className="text-gray-500 text-xs">({u.version_code})</span>
        </td>
        <td className="px-4 py-3">
          {u.is_active ? (
            <span className="text-xs bg-green-900/50 text-green-400 px-2 py-0.5 rounded font-semibold">Current</span>
          ) : (
            <span className="text-xs bg-gray-700 text-gray-400 px-2 py-0.5 rounded">Available</span>
          )}
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <ArtifactStatusBadge artifact={u.artifact} />
          <button
            type="button"
            onClick={() => toggleRelease(u.id)}
            className="ml-2 text-xs text-blue-400 hover:text-blue-300"
            aria-expanded={expandedReleases.has(u.id)}
          >
            {expandedReleases.has(u.id) ? 'Hide details' : 'Details'}
          </button>
        </td>
        <td className="px-4 py-3 text-gray-400">{formatBytes(u.file_size_bytes)}</td>
        <td className="px-4 py-3 text-gray-400">
          {editingNotesId === u.id ? (
            <div className="min-w-64">
              <textarea
                value={editingNotes}
                onChange={(e) => setEditingNotes(e.target.value)}
                rows={3}
                className="w-full rounded border border-gray-600 bg-gray-800 px-2 py-1 text-sm text-white"
                autoFocus
              />
              <div className="mt-2 flex gap-3 text-sm">
                <button
                  type="button"
                  onClick={() => saveEditingNotes(u)}
                  disabled={savingNotesId === u.id}
                  className="font-semibold text-green-400 hover:text-green-300 disabled:opacity-50"
                >
                  {savingNotesId === u.id ? 'Saving...' : 'Save'}
                </button>
                <button type="button" onClick={cancelEditingNotes} className="font-semibold text-gray-400 hover:text-gray-300">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <span>{u.release_notes || '—'}</span>
              <button type="button" onClick={() => startEditingNotes(u)} className="shrink-0 text-blue-400 hover:text-blue-300" title="Edit release notes">
                Edit
              </button>
            </div>
          )}
        </td>
        <td className="px-4 py-3 text-gray-400 text-xs">{formatTimestamp(u.created_at)}</td>
        <td className="px-4 py-3 text-right whitespace-nowrap">
          <ReleaseActions
            update={u}
            downloadUrl={downloadAppUpdateUrl(u.id)}
            pushing={pushingId === u.id}
            settingActive={rollingBackId === u.id}
            onPush={() => handlePushToModel(u)}
            onSetActive={() => handleSetActive(u)}
            onDelete={() => handleDelete(u)}
          />
        </td>
      </tr>
      {expandedReleases.has(u.id) && (
        <tr className="bg-gray-900/40">
          <td></td>
          <td colSpan={7} className="px-4 py-3">
            <ArtifactDetails update={u} />
          </td>
        </tr>
      )}
    </Fragment>
  );

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-bold">📦 APK Repository</h3>
          <p className="text-gray-400 text-sm mt-1">Builds available for OTA self-update, one per hardware model.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleOtaSelfCheck}
            disabled={otaTogglePending}
            title="While disabled, no radio can discover or install ANY active build (new or previously set) - re-enable this to let radios pick up whatever is currently marked Current."
            className={`font-bold py-2 px-4 rounded disabled:opacity-50 ${
              otaSelfCheckEnabled ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-yellow-600 hover:bg-yellow-700 text-white'
            }`}
          >
            {otaSelfCheckEnabled ? '⏸️ Disable OTA self-check' : '▶️ Enable OTA self-check'}
          </button>
          <button
            onClick={openAdd}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            + Publish APK
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-600 text-red-300 rounded-lg p-3">{error}</div>
      )}
      {info && (
        <div className="bg-blue-900/30 border border-blue-600 text-blue-300 rounded-lg p-3">{info}</div>
      )}
      {!otaSelfCheckEnabled && (
        <div className="bg-yellow-900/30 border border-yellow-600 text-yellow-300 rounded-lg p-3 text-sm">
          OTA self-check is disabled - no radio will discover or install any build automatically, even one already marked "Current". Use "Set as Current" to stage the build you want, then re-enable OTA self-check when you're ready to push it.
        </div>
      )}

      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-900/60 text-gray-400">
            <tr>
              <th className="px-4 py-3 text-left">Model</th>
              <th className="px-4 py-3 text-left">Version</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Artifact</th>
              <th className="px-4 py-3 text-left">Size</th>
              <th className="px-4 py-3 text-left">Release Notes</th>
              <th className="px-4 py-3 text-left">Published</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {groupedUpdates.map((group) => {
              const hasHistory = group.history.length > 0;
              const expanded = hasHistory && expandedModels.has(group.deviceModelId);
              return (
                <Fragment key={group.deviceModelId}>
                  {renderReleaseRow(group.primary, { modelName: group.modelName, deviceModelId: group.deviceModelId, hasHistory, expanded })}
                  {expanded && group.history.map((u) => renderReleaseRow(u, { indent: true }))}
                </Fragment>
              );
            })}
            {groupedUpdates.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                  No APKs published yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div data-management-modal className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-lg max-h-full overflow-y-auto p-6 space-y-4">
            {published ? (
              <PublishResult update={published} onClose={closeForm} />
            ) : (
              <>
                <h3 className="text-2xl font-bold text-white">Publish APK</h3>
                <UploadForm key={formKey} models={models} uploading={uploading} error={uploadError} onSubmit={handleUpload} onCancel={closeForm} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
